import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { LEGAL_DOCUMENTS } from './documents.config';
import { LegalProse } from './legal-prose';
import { resolveLocale } from './locale';
import { useLegalContent } from './use-legal-content';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { useRecordAcceptance, useRequiredAcceptances } from '@/hooks/use-legal';
import { useSignOut } from '@/hooks/use-sign-out';

/**
 * EULA acceptance gate (ESCLOUD-79 / ESCLOUD-181): while the gate query reports
 * an unaccepted EULA version (first login or material version change), a
 * blocking modal shows the full EULA text with Accept / Decline. Decline logs
 * the user out; Accept records the acceptance server-side and refetches the gate
 * state.
 *
 * Mounted by the two shell layouts (RootLayout, FullscreenLayout), which is
 * where its place in the gating chain is declared and checked — see
 * `GATING_CHAIN` in `router/index.tsx`. It is an overlay rather than a route
 * element on purpose: it renders *beside* the page, so the page below stays
 * rendered while the gate query resolves and while the gate fails open.
 *
 * **The onboarding funnel is exempt, and this component no longer knows it**
 * (ESCLOUD-286/288). Its plan screen carries the acceptance as its own checkbox
 * — the design has no dedicated legal step — and a blocking dialog on top would
 * be a second gate for the same document. That exemption used to be four
 * hardcoded route patterns here, which could disagree with the route tree
 * without anything failing. The funnel screens render their own full-page shell
 * and match no layout route, so they carry no overlay because of where they
 * live; `router.spec.tsx` sweeps the whole authenticated tree and names the
 * exempt set, so a route that gains or loses the overlay fails a test.
 */
export function EulaGate() {
    const { data: requiredAcceptances, isError, error } = useRequiredAcceptances();
    const requiredEula = requiredAcceptances?.find((required) => required.documentType === 'eula');

    // Every authenticated identity has a user row (ESCLOUD-331), so the gate
    // endpoint answers for everyone: any error here is a real gate outage,
    // handled by the fail-open path below.

    useEffect(() => {
        if (!isError) return;
        // Fail open, loudly: availability of clinical functions must not depend
        // on the gate endpoint being up (documented in SDS-CLI-015), but every
        // bypass must leave a signal. Server-side the failure is a logged 500
        // (a registry misconfiguration even fails the API boot); client-side
        // there is no telemetry pipeline yet, so a persistent toast makes the
        // bypass visible to the person best placed to report it.
        console.error('EULA gate query failed — failing open (acceptance not enforced)', error);
        toast.warning('Licence agreement status could not be verified', {
            id: 'eula-gate-fail-open', // stable id: re-renders update, never stack
            duration: Infinity,
            description:
                'You can keep working. Please reload the application; if this message ' +
                'persists, contact contact@therenva.com.',
        });
    }, [isError, error]);

    useEffect(() => {
        // Recovered (e.g. a retry or refetch succeeded): withdraw the warning.
        if (requiredAcceptances !== undefined) toast.dismiss('eula-gate-fail-open');
    }, [requiredAcceptances]);

    if (!requiredEula) {
        // Gate state unknown (still loading), gate endpoint down (logged above),
        // or nothing to accept: don't block.
        return null;
    }
    return <EulaDialog documentVersion={requiredEula.documentVersion} />;
}

function EulaDialog({ documentVersion }: { documentVersion: string }) {
    const config = LEGAL_DOCUMENTS.eula;
    const locale = resolveLocale(config.locales);
    const {
        data: eulaHtml,
        isPending: contentPending,
        isError: contentError,
    } = useLegalContent('eula', locale);
    const recordAcceptance = useRecordAcceptance();
    const signOut = useSignOut();
    const [declining, setDeclining] = useState(false);

    const accept = () => {
        recordAcceptance.mutate({ documentType: 'eula', documentVersion });
    };

    const decline = () => {
        setDeclining(true);
        signOut();
    };

    return (
        <Dialog open>
            {/* Blocking: no onOpenChange, no close button target — the only ways out are the two buttons. */}
            <DialogContent
                className="flex max-h-[85vh] flex-col sm:max-w-2xl [&>button]:hidden"
                onPointerDownOutside={(event) => event.preventDefault()}
                onEscapeKeyDown={(event) => event.preventDefault()}
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>End-User Licence Agreement</DialogTitle>
                    <DialogDescription>
                        Version {documentVersion} — effective {config.effectiveDate}. Please read and accept
                        the agreement to access ESCloud&apos;s clinical functions.
                    </DialogDescription>
                </DialogHeader>

                <div className="min-h-0 flex-1 overflow-y-auto rounded-md border p-4">
                    {contentPending && (
                        <div className="flex justify-center py-8">
                            <Spinner />
                        </div>
                    )}
                    {contentError && (
                        <p className="py-8 text-center text-sm text-destructive">
                            The agreement text could not be loaded, so it cannot be accepted. Please reload
                            the application or contact contact@therenva.com.
                        </p>
                    )}
                    {eulaHtml !== undefined && <LegalProse html={eulaHtml} />}
                </div>

                {recordAcceptance.isError && (
                    <p className="text-sm text-destructive">
                        Your acceptance could not be recorded. Please try again.
                    </p>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={decline} disabled={declining}>
                        Decline and sign out
                    </Button>
                    <Button
                        onClick={accept}
                        // Accept requires the agreement text to have actually rendered:
                        // an acceptance of a document that never displayed is not valid
                        // legal evidence.
                        disabled={recordAcceptance.isPending || eulaHtml === undefined || declining}
                    >
                        {recordAcceptance.isPending ? 'Recording…' : 'Accept'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
